import React, { useEffect, useRef, useState } from "react";
import "./OpalSearchSuggestion.css";
import TauriImage from "../../../../TauriImage/TauriImage";
import { getAll, getRecordById, updateRecord } from "../../../../../localDB";
import ItemDate from "./ItemDate/ItemDate";
import { createPortal } from "react-dom";
import TauriLoader2 from "../../../../TauriLoader2/TauriLoader2";
import OpalAdvancedCallModal from "../../../../OpalAdvancedCallModal/OpalAdvancedCallModal";
import config from '../../../../../config.json';
import OpalAlertModal from "../../../../OpalAlertModal/OpalAlertModal";
import { useBaseProvider } from "../../../../../BaseManager/BaseManager";
import { sessionType, statusMapping, SyncStatusMap } from "../../../../../visualizations/visualizations";
import { useNavigate } from "react-router-dom";
import appURL from '../../../../../appURL.json';
import OpalIncidentalCallModal from "../../../../OpalIncidentalCallModal/OpalIncidentalCallModal";

const OpalSearchSuggestion = ({ show, setShowSuggestion, search, setSearch, setSelectedDate }) => {
    const suggestionRef = useRef();
    const itemDateRef = useRef();
    const [loading, setLoading] = useState(false);
    const [loadingConfirm, setLoadingConfirm] = useState(false);
    const [itemList, setItemList] = useState([]);
    const [sessions, setSessions] = useState([]); //for list of dates with session id
    const [selectedSession, setSelectedSession] = useState(null) // store the selected session when date selected
    const [sessionTime, setSessionTime] = useState(null);
    const [hoveredAccount, setHoveredAccount] = useState(null);
    const [hoveredPosition, setHoveredPosition] = useState(null);
    const [visibleCount, setVisibleCount] = useState(6);
    const [showAdvancedCall, setShowAdvancedCall] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [timeIn, setTimeIn] = useState(false);
    const [timeOut, setTimeOut] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const [showIncidental, setShowIncidental] = useState(false);

    const navigate = useNavigate();

    const { isSync, setIsSidebarCollapsed } = useBaseProvider();

    const isFirstSearch = useRef(true);

    const storedPermission = localStorage.getItem("permission");
    const permission = JSON.parse(atob(storedPermission));

    const getToday = () => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        return now;
    };

    const highlightText = (text, query) => {
        if (!query) return text;
        const regex = new RegExp(`(${query})`, "gi");
        return text.split(regex).map((part, index) =>
            part.toLowerCase() === query.toLowerCase() ? (
                <span key={index} className="highlighted-text">{part}</span>
            ) : (
                part
            )
        );
    };

    const fetchData = async (search) => {
        try {
            if (!search || search === "") {
                setItemList([]);
                return
            }

            setLoading(true);
            const customerList = await getAll("customer");
            const term = search.toLowerCase();

            // Filter customers based on search term
            const filtered = customerList.filter((item) =>
                item?.SyncStatus !== SyncStatusMap.done &&
                (
                    item.Code?.toLowerCase().includes(term) ||
                    item.Name?.toLowerCase().includes(term)
                )
            ).sort((a, b) => a.Name.localeCompare(b.Name));

            // Only show suggestions if there are matching items
            if (filtered.length > 0) {
                setItemList(filtered);
            } else {
                setItemList([]);  // Clear the list if no match found
            }
        } catch (error) {
            console.log('Error fetching customer data!');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const checkAttendance = async () => {
            try {
                const todayDate = new Date();
                todayDate.setHours(0, 0, 0, 0);

                const attendance = await getAll("Attendance");
                const todayAttendance = attendance
                    .filter(a => a.SyncStatus !== SyncStatusMap.done)
                    .find((a) => {
                        const timeInDate = new Date(a?.TimeIn);
                        timeInDate.setHours(0, 0, 0, 0); // normalize

                        return timeInDate.getTime() === todayDate.getTime();
                    });

                setTimeIn(todayAttendance?.TimeIn ? true : false);
                setTimeOut(todayAttendance?.TimeOut ? true : false);

            } catch (error) {
                setValidationMessage("attendance not found!");
                setShowValidation(true);
            }
        };

        checkAttendance();
    }, [])

    const fetchSessionDates = async (customerID) => {
        try {

            const cpID = localStorage.getItem("c_id");
            const sessions = await getAll("session");
            const cust_ = await getRecordById("customer", customerID);
            const today = getToday();

            const filteredSessions = sessions
                .filter((session) => {
                    const sessionDate = new Date(session.SessionDate);
                    sessionDate.setHours(0, 0, 0, 0);  // Normalize the session date to 00:00:00

                    // Return the condition as true or false
                    return (
                        session.SyncStatus !== SyncStatusMap.done && 
                        session.AccountID === customerID &&
                        session.CyclePeriodID === Number(cpID) &&
                        session.Tag == null
                    );
                })
                .sort((a, b) => new Date(a.SessionDate) - new Date(b.SessionDate));

            setSessions(
                filteredSessions.map(s => ({
                    SessionDate: s.SessionDate,
                    ID: s.ID,
                    AccountID: s.AccountID,
                    Status: s.Status
                }))
            );
            setSessionTime(cust_.Time_call);
        } catch (error) {
            console.log("Error fetching session dates!");
        }
    };

    useEffect(() => {
        if (!show) return;

        const delay = isFirstSearch.current ? 2000 : 350;

        const delayDebounce = setTimeout(() => {
            if (!search || search.trim() === "" || search.length < 3) {
                setItemList([]);
                setShowSuggestion(false); // Hide when search is empty
                return;
            }

            fetchData(search);
            setShowSuggestion(true); // Show if there's valid input
            setVisibleCount(6);

            isFirstSearch.current = false; // Mark subsequent calls as non-first
        }, 350);

        return () => clearTimeout(delayDebounce); // Cleanup
    }, [search]);

    const handleUnplannedClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!permission?.coverage?.IncidentalJointCall) {
            setValidationMessage("You don't have the permission to do this action.");
            setShowValidation(true);
            return;
        } else if (!timeIn) {
            setValidationMessage("You have to do Start of Day before starting to do a Session.");
            setShowValidation(true);
            return;
        } else if (timeOut) {
            setValidationMessage("You have already ended your day of work today. You may do this action on the next day.");
            setShowValidation(true);
            return;
        } else if (isSync) {
            setValidationMessage("Your data is syncing.");
            setShowValidation(true);
            return;
        }

        setShowIncidental(true);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            const isOutsideSuggestion = suggestionRef.current && !suggestionRef.current.contains(event.target);
            const isOutsideItemDate = itemDateRef.current && !itemDateRef.current.contains(event.target);

            let isOutsideModal = true;
            let isOutsideIncidentalModal = true;
            let isOutsideConfirmModal = true;

            if (showAdvancedCall) {
                const modal = document.querySelector('.opal-advancedcall-modal');
                // Only update isOutsideModal if modal exists, otherwise keep it as false (not outside)
                if (modal) {
                    isOutsideModal = !modal.contains(event.target);
                } else {
                    isOutsideModal = false;
                }
            }

            if (showIncidental) {
                const modalInci = document.querySelector('.opal-incidental-modal');
                if (modalInci) {
                    isOutsideIncidentalModal = !modalInci.contains(event.target);
                } else {
                    isOutsideIncidentalModal = false;
                }
            }

            if (showConfirm) {
                const modal_alert = document.querySelector('.opal-alert-modal');
                if (modal_alert) {
                    isOutsideConfirmModal = !modal_alert.contains(event.target);
                } else {
                    isOutsideConfirmModal = false;
                }
            }

            if (isOutsideSuggestion && isOutsideItemDate && isOutsideModal && isOutsideConfirmModal && isOutsideIncidentalModal) {
                setShowSuggestion(false);
            }
        };

        if (show) {
            document.addEventListener("mousedown", handleClickOutside, true);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside, true);
        };
    }, [show, showAdvancedCall, setShowSuggestion, showConfirm, showIncidental]);

    const getLocation = async () => {
        return new Promise((resolve, reject) => {

            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    resolve({ latitude, longitude });
                },
                (error) => {
                    console.error(error);
                    reject("Unable to retrieve location");
                },
                {
                    enableHighAccuracy: true
                }
            );
        });
    };

    const OnlineStartSession = async (a) => {
        try {
            const { latitude, longitude } = await getLocation();

            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");

            const requestBody = {
                ID: a.ID.toString(),
                VersionID: a?.VersionID,
                Location: {
                    lat: latitude,
                    long: longitude
                }
            };

            const response = await fetch(appURL.API_StartSession, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                    "X-ETR-DeviceID": devId
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (!response.ok) {
                if (data?.code) {
                    return { error: data?.description || "Session failed to start." };
                } else {
                    return { error: "Unexpected server error." };
                }
            }

            return { serverDate: data?.server_date };

        } catch (error) {
            console.error("Error starting session:", error);
            return { error: "Network or system error." };
        }
    };

    const onDateClick = async (session, type) => {
        const storedPermission = localStorage.getItem("permission");
        const permission = JSON.parse(atob(storedPermission));

        if (!permission?.coverage?.advanced_calls) {
            setValidationMessage("You don't have the permission to do this action.");
            setShowValidation(true);
            return;
        } else if (!timeIn) {
            setValidationMessage("You have to do Start of Day before starting to do a Session.");
            setShowValidation(true);
            return;
        } else if (timeOut) {
            setValidationMessage("You have already ended your day of work today. You may do this action on the next day.");
            setShowValidation(true);
            return;
        } else if (isSync) {
            setValidationMessage("Your data is syncing.");
            setShowValidation(true);
            return;
        }

        setSelectedSession(session);

        if (type === sessionType.PastDue) {
            setShowSuggestion(false);

            const sessDate = new Date(session.SessionDate);
            sessDate.setHours(0, 0, 0, 0);
            localStorage.setItem("dateSelect", JSON.stringify(sessDate));

            //to prevent the initial load to select the first item in sidebar 
            window.dispatchEvent(new Event("dateSelect"));

            // setIsSidebarCollapsed(true);
            localStorage.setItem("preventNavigation", true);
            setTimeout(() => {
                window.dispatchEvent(new Event("session"));
            }, 100);
            navigate(`/coverage/${session.ID}`);
        }
        if (type === sessionType.Advanced) {
            if (session.Status !== statusMapping.pending) {
                setShowSuggestion(false);

                const sessDate = new Date(session.SessionDate);
                sessDate.setHours(0, 0, 0, 0);
                localStorage.setItem("dateSelect", JSON.stringify(sessDate));

                //to prevent the initial load to select the first item in sidebar 
                window.dispatchEvent(new Event("dateSelect"));

                // setIsSidebarCollapsed(true);
                localStorage.setItem("preventNavigation", true);
                setTimeout(() => {
                    window.dispatchEvent(new Event("session"));
                }, 100);
                navigate(`/coverage/${session.ID}`);
            }
            setShowAdvancedCall(true);
        }
    }

    const StartRecoverySession = async () => {
        try {
            setLoadingConfirm(true);
            const isOnline = JSON.parse(localStorage.getItem("isOnline"));
            const hasInternet = navigator.onLine;

            const sessionOrig = await getRecordById("session", selectedSession.ID);

            sessionOrig.SyncStatus = SyncStatusMap.update;

            sessionOrig.Status = statusMapping.inprogress;

            const sessDate = new Date(sessionOrig.SessionDate);
            sessDate.setHours(0, 0, 0, 0);
            localStorage.setItem("dateSelect", JSON.stringify(sessDate));

            let result = null;
            if (isOnline && hasInternet) {
                result = await OnlineStartSession(sessionOrig);
                if (result?.error) {
                    return; //
                }
            }

            sessionOrig.ActualStart = result?.serverDate ?? new Date().toISOString();
            await updateRecord('session', sessionOrig);

            setShowSuggestion(false);

            //to prevent the initial load to select the first item in sidebar 
            window.dispatchEvent(new Event("dateSelect"));

            // setIsSidebarCollapsed(true);
            localStorage.setItem("preventNavigation", true);
            setTimeout(() => {
                window.dispatchEvent(new Event("session"));
            }, 100);
            navigate(`/coverage/${sessionOrig.ID}`);
            setIsSidebarCollapsed(true);

        } catch (error) {

        } finally {
            setLoadingConfirm(false);
        }
    }

    const handleDone = (done) => {
        if (done) {
            setShowSuggestion(false);
        }
    }


    if (!show || itemList?.length === 0) return null;

    const displayedItems = itemList.slice(0, visibleCount);

    return (
        <div className="opal-search-suggestion d-none d-md-block" style={{ '--opal-search-suggestion-height': itemList?.length < visibleCount ? 'auto' : '15.4rem' }} ref={suggestionRef}>
            {loading ? (
                <TauriLoader2 width="1.5rem" height="1.5rem" borderwidth="0.2rem" color="#1D5D9B" />
            )
                : (
                    <div className="opal-suggestion-container">
                        <div className="opal-table">
                            {displayedItems.map((item, index) => (
                                <div
                                    key={item.ID}
                                    className={`opal-table-row ${index % 2 === 0 ? '' : 'striped-color'} ${hoveredAccount?.ID === item.ID ? 'active-row' : ''}`}
                                    // onClick={() => onSelect(item?.Name)}
                                    onMouseEnter={(e) => {
                                        setHoveredAccount(item);
                                        fetchSessionDates(item.ID);

                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setHoveredPosition({
                                            top: rect.top + window.scrollY,
                                            left: rect.right + 10, // show on right side
                                        });
                                    }}
                                >
                                    <div className="opal-table-cell code-column">
                                        {highlightText(item?.Code, search)}
                                    </div>
                                    <div className="opal-table-cell name-column">
                                        {highlightText(item?.Name, search)}
                                    </div>
                                    <div className="opal-table-cell-icon">
                                        <TauriImage localImage={"/Images/arrow-right.svg"} alt="icon" />
                                    </div>

                                </div>
                            ))}
                            {visibleCount < itemList.length && (
                                <div className="see-more" onClick={() => setVisibleCount(prev => prev + 6)}>
                                    <span>See More</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

            {hoveredAccount && hoveredPosition &&
                createPortal(
                    <ItemDate
                        ref={itemDateRef}
                        sessions={sessions}
                        sessTime={sessionTime}
                        onDateClick={onDateClick}
                        hoveredAccount={hoveredAccount}
                        showUnplanned={handleUnplannedClick}
                        style={{
                            position: 'absolute',
                            top: hoveredPosition.top,
                            left: hoveredPosition.left,
                            zIndex: 5,
                        }} />,
                    document.body
                )
            }

            <OpalAdvancedCallModal
                show={showAdvancedCall}
                onHide={() => setShowAdvancedCall(false)}
                session={selectedSession}
                onDone={handleDone}
            />

            <OpalAlertModal
                show={showValidation}
                onHide={() => {
                    setShowValidation(false);
                }}
                title={"Message"}
                bodyText={validationMessage}
                modalType="message"
            />
            <OpalAlertModal
                show={showConfirm}
                onHide={() => setShowConfirm(false)}
                title={"Start Recovery"}
                bodyText={"Are you sure you want to start the session?"}
                onConfirm={StartRecoverySession}
                confirmButtonText={"Yes! Start Recovery"}
                cancelButtonText="No"
                modalType="question"
                loading={loadingConfirm}
            />
            <OpalIncidentalCallModal
                show={showIncidental}
                onHide={() => setShowIncidental(false)}
                account={hoveredAccount}
            />
        </div>
    );
};

export default OpalSearchSuggestion;
